"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";

import { formatMs } from "@/lib/utils";

interface VoiceMessagePlayerProps {
  audioUrl: string;
  audioDuration?: number;
  audioMimeType?: string;
  audioWaveform?: number[] | null;
}

const PLACEHOLDER_BARS = Array.from({ length: 32 }, (_, i) =>
  Math.round(20 + Math.abs(Math.sin(i * 0.8)) * 60),
);


export default function VoiceMessagePlayer({
  audioUrl,
  audioDuration,
  audioMimeType,
  audioWaveform,
}: VoiceMessagePlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentMs, setCurrentMs] = useState(0);
  const [totalMs, setTotalMs] = useState(audioDuration ?? 0);

  const bars = audioWaveform && audioWaveform.length > 0 ? audioWaveform : PLACEHOLDER_BARS;

  // Progress ratio (0–1) used to highlight played bars.
  const progress = totalMs > 0 ? currentMs / totalMs : 0;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => setCurrentMs(audio.currentTime * 1000);
    const onDurationChange = () => {
      if (isFinite(audio.duration)) setTotalMs(audio.duration * 1000);
    };
    const onEnded = () => {
      setIsPlaying(false);
      setCurrentMs(0);
    };

    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("durationchange", onDurationChange);
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("durationchange", onDurationChange);
      audio.removeEventListener("ended", onEnded);
    };
  }, []);

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      await audio.play();
      setIsPlaying(true);
    }
  }, [isPlaying]);

  // Seek on bar click.
  const handleBarClick = useCallback(
    (index: number) => {
      const audio = audioRef.current;
      if (!audio || !isFinite(audio.duration)) return;
      const ratio = index / bars.length;
      audio.currentTime = audio.duration * ratio;
      setCurrentMs(audio.duration * ratio * 1000);
    },
    [bars.length],
  );

  return (
    <div className="flex w-full max-w-xs items-center gap-2 rounded-full bg-muted px-3 py-2">
      {/* Hidden native audio element */}
      <audio ref={audioRef} src={audioUrl} preload="metadata" />

      {/* Play / Pause button */}
      <button
        type="button"
        onClick={togglePlay}
        className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-opacity hover:opacity-80"
        aria-label={isPlaying ? "Pause" : "Play"}
      >
        {isPlaying ? (
          <Pause className="h-4 w-4" />
        ) : (
          <Play className="h-4 w-4 translate-x-0.5" />
        )}
      </button>

      {/* Waveform bars */}
      <div
        className="flex flex-1 cursor-pointer items-end gap-px"
        style={{ height: "28px" }}
        aria-label="Audio waveform"
      >
        {bars.map((peak, i) => {
          const played = i / bars.length < progress;
          const heightPct = Math.max(15, Math.min(100, peak));
          return (
            <div
              key={i}
              role="button"
              tabIndex={-1}
              onClick={() => handleBarClick(i)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleBarClick(i);
                }
              }}
              className={`w-1 flex-1 rounded-sm transition-colors ${
                played ? "bg-primary" : "bg-muted-foreground/40"
              }`}
              style={{ height: `${heightPct}%` }}
            />
          );
        })}
      </div>

      {/* Time display */}
      <span className="flex-shrink-0 text-xs tabular-nums text-muted-foreground">
        {formatMs(currentMs > 0 ? currentMs : totalMs)}
      </span>
    </div>
  );
}
